"use client";

import { useState } from "react";
import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/TopBar";
import useIsMobile from "../../components/shared/Mobile-Responsive";
import Notification from "./Notification";
import Security from "./Security";
import Preferance from "./Preferance";
import Privacy from "./Privacy";
import Support from "../../../member/components/Support";
import Deactivate from "./Deactivate";
import OrganizationConfiguration from "../../components/OrganizationConfiguration/OrganizationConfiguration";
import { ToastContainer } from "react-toastify";
import {
  User,
  Users,
  Bell,
  Shield,
  Settings as SettingsIcon,
  FileText,
  LogOut,
} from "lucide-react";

export default function Index() {
  const [activeSettingTab, setActiveSettingTab] = useState("general");
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();

  // Settings navigation items
  const settingsNavItems = [
    { id: "general", label: "General Settings", icon: SettingsIcon, active: true },
    { id: "notifications", label: "Notification Settings", icon: Bell },
    { id: "security", label: "Account & Security", icon: Shield },
    { id: "preferences", label: "Preferences", icon: SettingsIcon },
    { id: "privacy", label: "Privacy & Data", icon: FileText },
    { id: "support", label: "Support & Legal", icon: FileText },
    { id: "deactivate", label: "Deactivate / Delete Account", icon: LogOut },
  ];

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 flex bg-[#F8FAFC] overflow-hidden">
      <TopBar />

      {/* Main Sidebar */}
      <div
        className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}
      >
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 min-w-0 overflow-auto bg-[#F8FAFC] pt-2 px-2 pb-8 transition-all duration-300 ${
          collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"
        }`}
      >
        <div className="p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl">
          {/* Header */}
          {isMobile ? (
            <>
              <div className="flex items-center justify-between gap-2 pb-3">
                <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Settings</h1>
              </div>
            </>
          ) : (
            <>
              <div className="w-full mx-auto pl-[10px] mb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Settings</h1>
                </div>
              </div>
            </>
          )}

        {/* Settings Inner Sidebar - Mobile Horizontal Scroll */}
        <div className="md:hidden bg-white rounded-t-[10px] overflow-x-auto scrollbar-hide mb-3">
          <div className="flex whitespace-nowrap px-2">
            {settingsNavItems.map((item) => (
              <div
                key={item.id}
                className={`py-3 px-3 sm:py-4 sm:px-4 flex-shrink-0 border-b-2 cursor-pointer transition-colors ${
                  item.id === activeSettingTab
                    ? "border-[#2563EB] text-[#2563EB] font-medium"
                    : "border-transparent text-[#64748B] hover:text-[#2563EB]"
                }`}
                onClick={() => setActiveSettingTab(item.id)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-poppins text-[12px] sm:text-[14px] whitespace-nowrap">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 items-start">
          {/* Settings Inner Sidebar - Desktop and Tablet */}
          <div className="hidden md:block w-60 flex-shrink-0 bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[10px]">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-[#94A3B8] font-poppins px-[10px] pt-[6px] pb-[8px] mb-0">
              Settings
            </p>
            <div className="space-y-1">
              {settingsNavItems.map((item) => {
                const ItemIcon = item.icon;
                const isActive = item.id === activeSettingTab;
                const isDanger = item.id === "deactivate";
                return (
                  <div
                    key={item.id}
                    className={`flex items-center font-poppins gap-2.5 min-h-[40px] px-[10px] py-[8px] rounded-xl cursor-pointer transition-colors ${
                      isActive
                        ? "bg-[#0F172A] text-white font-semibold shadow-md"
                        : isDanger
                          ? "text-[#DC2626] hover:bg-[#FEF2F2]"
                          : "text-[#334155] hover:bg-[#F1F5F9]"
                    }`}
                    onClick={() => setActiveSettingTab(item.id)}
                  >
                    <ItemIcon size={16} className="flex-shrink-0" />
                    <span className="font-poppins text-[13px] leading-tight">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* General Settings Content */}
          {activeSettingTab === "general" && (
            <div className="flex-1 min-w-0 w-full bg-white pb-[40px] p-[20px] rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm overflow-x-auto">
              <SettingsHeader />
              <SettingsDescription />
              <div className="w-full max-w-none">
                <OrganizationConfiguration />
              </div>
            </div>
          )}
          {activeSettingTab === "notifications" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen bg-[#FFFFFF] py-0 w-full">
                <Notification />
              </main>
            </div>
          )}
          {activeSettingTab === "security" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen bg-[#FFFFFF] w-full max-w-none lg:max-w-[50%] xl:max-w-[60%]">
                <Security />
              </main>
            </div>
          )}
          {activeSettingTab === "preferences" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen bg-[#FFFFFF] w-full max-w-none lg:max-w-[50%] xl:max-w-[60%]">
                <Preferance />
              </main>
            </div>
          )}
          {activeSettingTab === "privacy" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen bg-[#FFFFFF] w-full max-w-none lg:max-w-[50%] xl:max-w-[60%]">
                <Privacy />
              </main>
            </div>
          )}
          {activeSettingTab === "support" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen bg-[#FFFFFF] w-full">
                <Support fullWidth />
              </main>
            </div>
          )}
          {activeSettingTab === "deactivate" && (
            <div className="flex-1 min-w-0 w-full bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm p-[20px]">
              <main className="min-h-screen w-full max-w-none lg:max-w-[50%] xl:max-w-[60%]">
                <Deactivate />
              </main>
            </div>
          )}
        </div>
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

function SettingsHeader() {
  return (
    <div className="flex justify-between items-center mb-1 gap-3">
      <div className="flex items-center gap-2.5">
        <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-[#EFF6FF] text-[#2563EB] flex-shrink-0">
          <SettingsIcon size={18} />
        </div>
        <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">
          General Settings
        </h1>
      </div>
    </div>
  );
}

function SettingsDescription() {
  return (
    <>
      <p className="text-[#64748B] text-xs font-poppins font-normal mb-4 ml-[46px] leading-relaxed">
        Manage your organization settings, preferences, and account configuration.
      </p>
      <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9] mt-0 mb-4" />
    </>
  );
}

